export type Facing = 'user' | 'environment';

export interface CameraHandle {
  video: HTMLVideoElement;
  /** current video track (changes when switching cameras) */
  videoTrack(): MediaStreamTrack;
  audio: MediaStreamTrack | null;
  facing: Facing;
  /** front ↔ rear; resolves with the new facing */
  switchFacing(to?: Facing): Promise<Facing>;
  stop(): void;
}

const videoConstraints = (facing: Facing): MediaTrackConstraints => ({
  facingMode: facing,
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
});

/**
 * In a dim room Android cameras stretch the exposure and drop to ~15 fps, which makes
 * every motion (and the cut-out following it) look laggy. Ask for at least 24 fps: the
 * camera raises the gain instead (a little grain, smooth motion). Best effort.
 */
async function keepFrameRate(t: MediaStreamTrack) {
  try { await t.applyConstraints({ ...t.getConstraints(), frameRate: { min: 24, ideal: 30, max: 30 } }); } catch { /* not supported: keep it */ }
}

export async function openCamera(withAudio = true, facing: Facing = 'user'): Promise<CameraHandle> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Trình duyệt không hỗ trợ camera (cần HTTPS hoặc localhost).');
  }
  const stream = await navigator.mediaDevices.getUserMedia({
    video: videoConstraints(facing),
    audio: withAudio
      ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      : false,
  }).catch(async (e) => {
    if (!withAudio) throw e;
    // mic denied → continue with video only
    return navigator.mediaDevices.getUserMedia({ video: videoConstraints(facing), audio: false });
  });
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  let vtrack = stream.getVideoTracks()[0];
  await keepFrameRate(vtrack);
  video.srcObject = new MediaStream([vtrack]);
  await video.play();
  if (!video.videoWidth) await new Promise((r) => video.addEventListener('loadedmetadata', r, { once: true }));

  const handle: CameraHandle = {
    video,
    videoTrack: () => vtrack,
    audio: stream.getAudioTracks()[0] ?? null,
    facing,
    async switchFacing(to) {
      const next: Facing = to ?? (handle.facing === 'user' ? 'environment' : 'user');
      if (next === handle.facing) return next;
      const s = await navigator.mediaDevices.getUserMedia({ video: videoConstraints(next), audio: false });
      vtrack.stop();
      vtrack = s.getVideoTracks()[0];
      await keepFrameRate(vtrack);
      video.srcObject = new MediaStream([vtrack]);
      await video.play();
      if (!video.videoWidth) await new Promise((r) => video.addEventListener('loadedmetadata', r, { once: true }));
      handle.facing = next;
      return next;
    },
    stop: () => {
      vtrack.stop();
      stream.getTracks().forEach((t) => t.stop());
    },
  };
  return handle;
}
